import { Role } from './enums';

export interface MenuItem {
  name: string;
  icon: string;
  link: string;
}

export interface MenuGroup {
  title: string;
  items: MenuItem[];
}

export interface NavigationContext {
  userId?: string;
  roles: Role[];
  rootRights?: string;
  usesDriverPlanning?: boolean;
  usesEmployeeListForStaff?: boolean;
  ignoreDriverStatus?: boolean;
  coDriverEnabled?: boolean;
}

export interface NavigationPermissions {
  hasAtLeastRole(roles: Role[] | undefined, requiredRole: Role): boolean;
  hasRole(roles: Role[] | undefined, targetRole: Role): boolean;
}

export function buildMenuGroups(
  context: NavigationContext,
  permissions: NavigationPermissions,
): MenuGroup[] {
  const { roles, rootRights, userId } = context;
  const groups: MenuGroup[] = [];
  const showAvailability =
    context.usesDriverPlanning !== false && context.ignoreDriverStatus !== true;
  const canAccessEmployeeList =
    rootRights === 'admin' ||
    permissions.hasRole(roles, Role.Administrator) ||
    ([Role.Planner, Role.RidePlanner, Role.Driver, Role.CoDriver, Role.Other].some((role) =>
      roles.includes(role),
    ) &&
      context.usesEmployeeListForStaff === true);

  if (roles.length > 0 || rootRights === 'admin') {
    const mainItems: MenuItem[] = [];

    if (permissions.hasAtLeastRole(roles, Role.RidePlanner)) {
      mainItems.push({ name: 'Planning', icon: 'calendar_month', link: '/plannen' });
    }

    if (showAvailability) {
      const isElevated =
        rootRights === 'admin' ||
        permissions.hasRole(roles, Role.Administrator) ||
        permissions.hasRole(roles, Role.Planner);
      let link: string | null = null;
      if (isElevated) {
        link = '/schedule';
      } else if (permissions.hasRole(roles, Role.RidePlanner)) {
        link = `/schedule/planner/${userId}`;
      } else if (permissions.hasRole(roles, Role.Driver)) {
        link = `/schedule/driver/${userId}`;
      } else if (permissions.hasRole(roles, Role.CoDriver) && context.coDriverEnabled !== false) {
        link = `/schedule/co-driver/${userId}`;
      }
      if (link) {
        mainItems.push({ name: 'Rooster', icon: 'perm_contact_calendar', link });
      }
    }

    if (permissions.hasAtLeastRole(roles, Role.RidePlanner)) {
      mainItems.push({ name: 'Passagiers', icon: 'people', link: '/users' });
    }

    if (permissions.hasAtLeastRole(roles, Role.Planner)) {
      mainItems.push({ name: 'Prullenbak', icon: 'delete', link: '/deleted-rides' });
    }

    if (mainItems.length > 0) {
      groups.push({ title: 'Menu', items: mainItems });
    }

    if (permissions.hasAtLeastRole(roles, Role.Planner) || canAccessEmployeeList) {
      const systemItems: MenuItem[] = [];

      if (permissions.hasAtLeastRole(roles, Role.Administrator)) {
        systemItems.push({
          name: 'Ritvoorbereiding',
          icon: 'edit_road',
          link: '/ride-preparation/standard-locations',
        });
        systemItems.push({ name: 'Vervoer', icon: 'commute', link: '/transport/vehicles' });
      }

      if (
        permissions.hasAtLeastRole(roles, Role.Administrator) &&
        context.ignoreDriverStatus !== true
      ) {
        systemItems.push({ name: 'Dagdelen', icon: 'edit_calendar', link: '/dayparts' });
      }

      if (canAccessEmployeeList) {
        systemItems.push({ name: 'Medewerkers', icon: 'manage_accounts', link: '/employees' });
      }

      if (permissions.hasAtLeastRole(roles, Role.Planner)) {
        systemItems.push({ name: 'Financiën', icon: 'payments', link: '/financial/audits' });
      }

      if (permissions.hasAtLeastRole(roles, Role.Administrator)) {
        systemItems.push({ name: 'Instellingen', icon: 'settings_filled', link: '/settings' });
      }

      if (rootRights === 'admin') {
        systemItems.push({
          name: 'Admin tools',
          icon: 'admin_panel_settings',
          link: '/admin-tools',
        });
      }

      groups.push({ title: 'Systeem', items: systemItems });
    }
  }

  return groups;
}
